import { ActionReducerMapBuilder, Draft, PayloadAction } from '@reduxjs/toolkit'
import {
  DataList,
  saveErrorInState,
  dismissError as commonDismissError,
  closeAlert as commonCloseAlert
} from '../common'
import { downloadFulfilled, DownloadAction } from '../page'
import type { SettingState } from './types'
import type {
  fetchSettings,
  createSetting,
  showSetting,
  modifySetting,
  deleteSetting,
  copySetting,
  exportSetting,
  createSettingDetail,
  modifySettingDetail,
  deleteSettingDetail,
  createArticleRulesGroup,
  updateArticleRulesGroup,
  deleteArticleRulesGroup,
  updateArticleRulesPageSetting
} from './settingAPI'

export const selectIdReducer = (state: Draft<SettingState>, action: PayloadAction<number>) => {
  state.selectedIds = Array.from(new Set(state.selectedIds).add(action.payload))
}

export const unselectIdReducer = (state: Draft<SettingState>, action: PayloadAction<number>) => {
  const set = new Set(state.selectedIds)
  set.delete(action.payload)
  state.selectedIds = Array.from(set)
}

export const clearSelectedIdsReducer = (state: Draft<SettingState>) => {
  state.selectedIds = []
}

export const openModalReducer = (state: Draft<SettingState>, action: PayloadAction<string | null>) => {
  state.openedModal = action.payload
}

export const closeModalReducer = (state: Draft<SettingState>) => {
  state.openedModal = null
}

export const selectTabReducer = (state: Draft<SettingState>, action: PayloadAction<string>) => {
  state.currentTab = action.payload
}

export const buildExtraReducers = (
  builder: ActionReducerMapBuilder<SettingState>,
  thunks: {
    fetchSettings: typeof fetchSettings
    createSetting: typeof createSetting
    showSetting: typeof showSetting
    modifySetting: typeof modifySetting
    deleteSetting: typeof deleteSetting
    copySetting: typeof copySetting
    exportSetting: typeof exportSetting
    createSettingDetail: typeof createSettingDetail
    modifySettingDetail: typeof modifySettingDetail
    deleteSettingDetail: typeof deleteSettingDetail
    createArticleRulesGroup: typeof createArticleRulesGroup
    updateArticleRulesGroup: typeof updateArticleRulesGroup
    deleteArticleRulesGroup: typeof deleteArticleRulesGroup
    updateArticleRulesPageSetting: typeof updateArticleRulesPageSetting
  }
) =>
  builder
    .addCase(thunks.fetchSettings.pending, DataList.fetchPending)
    .addCase(thunks.fetchSettings.fulfilled, DataList.fetchFulfilled)
    .addCase(thunks.fetchSettings.rejected, saveErrorInState(DataList.fetchRejected))
    .addCase(thunks.createSetting.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.createSetting.fulfilled, (state) => {
      state.fetching = false
      state.openedModal = null
    })
    .addCase(thunks.createSetting.rejected, (state, action) => {
      state.fetching = false
      const error = action.payload as { message: string }
      state.alert = { show: true, variant: 'danger', alertBody: error.message }
    })
    .addCase(thunks.showSetting.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.showSetting.fulfilled, (state, action) => {
      state.fetching = false
      state.setting = action.payload
    })
    .addCase(
      thunks.showSetting.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.modifySetting.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.modifySetting.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(
      thunks.modifySetting.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.deleteSetting.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.deleteSetting.fulfilled, (state) => {
      state.fetching = false
      state.selectedIds = []
    })
    .addCase(
      thunks.deleteSetting.rejected,
      saveErrorInState((state) => {
        state.fetching = false
        state.selectedIds = []
      })
    )
    .addCase(thunks.copySetting.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.copySetting.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(
      thunks.copySetting.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.exportSetting.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.exportSetting.fulfilled, (state, action) => {
      downloadFulfilled(state, action as unknown as DownloadAction)
      state.fetching = false
    })
    .addCase(
      thunks.exportSetting.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.createSettingDetail.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.createSettingDetail.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(thunks.createSettingDetail.rejected, (state, action) => {
      state.fetching = false
      const error = action.payload as { message: string }
      state.alert = { show: true, variant: 'danger', alertBody: error.message }
    })
    .addCase(thunks.modifySettingDetail.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.modifySettingDetail.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(
      thunks.modifySettingDetail.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.updateArticleRulesPageSetting.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.updateArticleRulesPageSetting.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(
      thunks.updateArticleRulesPageSetting.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.createArticleRulesGroup.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.createArticleRulesGroup.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(
      thunks.createArticleRulesGroup.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.updateArticleRulesGroup.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.updateArticleRulesGroup.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(
      thunks.updateArticleRulesGroup.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.deleteArticleRulesGroup.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.deleteArticleRulesGroup.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(
      thunks.deleteArticleRulesGroup.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.deleteSettingDetail.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.deleteSettingDetail.fulfilled, (state) => {
      state.fetching = false
      state.selectedIds = []
    })
    .addCase(
      thunks.deleteSettingDetail.rejected,
      saveErrorInState((state) => {
        state.fetching = false
        state.selectedIds = []
      })
    )

export { commonDismissError as dismissErrorReducer, commonCloseAlert as closeAlertReducer }
